// FLAGS: identity
// Runs the Google auth-code popup, exchanges the code for session cookies, and routes the response to
// either authenticator enrollment or the code step.
import { useGoogleLogin } from "@react-oauth/google";
import { toast } from "sonner";
import { Button } from "@openup/shared/primitives/ui/button";
import {
  useGoogleLoginMutation,
  useGoogleSignUpMutation,
} from "@src/queries/auth";
import { AUTHENTICATOR_KEY_PROVIDER } from "@src/models/auth";
import { env } from "@src/env";

interface GoogleSignInStepProps {
  onTwoFactorPending: () => void;
  onEnrollRequired: () => void;
  onRegistered: () => void;
}

// Inner component so the Google hook (which throws without a client id)
// only mounts when the app is configured.
const GoogleSignInButtons = ({
  onTwoFactorPending,
  onEnrollRequired,
  onRegistered,
}: GoogleSignInStepProps) => {
  const loginMutation = useGoogleLoginMutation();
  const signUpMutation = useGoogleSignUpMutation();

  const onGoogleSuccess = (code: string) => {
    loginMutation.mutate(code, {
      onSuccess: (response) => {
        // FLAGS: identity
        // Enrollment is forced whenever the account carries no AuthenticatorKey provider, including
        // when 2FA is off for it entirely. Drop that branch and such an account lands on a code
        // screen it can never satisfy.
        //
        // CustomerSuccessPolicy requires a verified authenticator, so enrollment comes first —
        // same behaviour as the CS dashboard.
        if (
          !response.userTokenProviders?.includes(AUTHENTICATOR_KEY_PROVIDER)
        ) {
          onEnrollRequired();
          return;
        }
        if (response.twoFactorVerification === "pending") {
          onTwoFactorPending();
        }
        // not_required with an enrolled authenticator: cookies are set;
        // useCurrentUser refetch redirects via PublicOnlyRoute.
      },
      onError: () => {
        toast.error("Google sign-in failed. Please try again.");
      },
    });
  };

  const onGoogleSignUpSuccess = (code: string) => {
    signUpMutation.mutate(code, {
      // The account is created but has no CustomerSuccess role yet — surface
      // the "contact your administrator" message instead of entering the app.
      onSuccess: () => onRegistered(),
      onError: () => {
        toast.error("Sign-up failed. Please try again.");
      },
    });
  };

  const startGoogleLogin = useGoogleLogin({
    flow: "auth-code",
    onSuccess: (response) => onGoogleSuccess(response.code),
    onError: () => toast.error("Google sign-in was cancelled or failed."),
  });

  const startGoogleSignUp = useGoogleLogin({
    flow: "auth-code",
    onSuccess: (response) => onGoogleSignUpSuccess(response.code),
    onError: () => toast.error("Google sign-up was cancelled or failed."),
  });

  const busy = loginMutation.isPending || signUpMutation.isPending;

  return (
    <div className="flex flex-col items-center gap-4">
      <Button
        variant="default"
        size="lg"
        onClick={() => startGoogleLogin()}
        disabled={busy}
      >
        {loginMutation.isPending ? "Signing in…" : "Sign in with Google"}
      </Button>

      <p className="text-sm text-gray-600">New to OpenUp Spaces?</p>

      <Button
        variant="outline"
        size="lg"
        onClick={() => startGoogleSignUp()}
        disabled={busy}
      >
        {signUpMutation.isPending ? "Signing up…" : "Sign up with Google"}
      </Button>
    </div>
  );
};

const GoogleSignInStep = ({
  onTwoFactorPending,
  onEnrollRequired,
  onRegistered,
}: GoogleSignInStepProps) => {
  if (!env.REACT_APP_GOOGLE_CLIENT_ID) {
    return (
      <p className="text-center text-gray-600" role="alert">
        Google sign-in is not configured: REACT_APP_GOOGLE_CLIENT_ID is missing.
        Set it in .env.development.local (see the README) and restart the dev
        server.
      </p>
    );
  }
  return (
    <GoogleSignInButtons
      onTwoFactorPending={onTwoFactorPending}
      onEnrollRequired={onEnrollRequired}
      onRegistered={onRegistered}
    />
  );
};

export default GoogleSignInStep;
